import React, { useCallback, useContext } from 'react';
import { Timeline, Row, Spin, Typography } from 'antd';
import { formatDate } from '@utils/formatters';
import { green, red } from '@ant-design/colors';
import { useTranslation } from 'react-i18next';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { IAction } from '@pages/Operational/ActionsQueue/types';
import useSwr from '@hooks/useSwr';
import Paragraph from 'antd/lib/typography/Paragraph';
import SessionContext from '@providers/Session/SessionContext';
import CustomCard from '@components/CustomCard';

const { Text } = Typography;

interface IDeviceActionsTimelineProps {
  id?: number | undefined;
}

const DeviceActionsTimeline: React.FC<IDeviceActionsTimelineProps> = ({
  id,
}) => {
  const { t } = useTranslation(['device-actions', 'inventory-detailed']);
  const { selectedApp, isMot } = useContext(SessionContext);

  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const { data } = useSwr<IAction[]>(
    `/service-proxy/inventory/${routeToFindData}/${id}/actions`,
  );

  const getDotByStatus = (status: number) => {
    switch (status) {
      case 1:
      case 2:
      case 3:
      case 4:
        return (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
        );
      case 0:
        return (
          <CheckCircleOutlined style={{ fontSize: 16, color: green[6] }} />
        );
      default:
        return (
          <ExclamationCircleOutlined style={{ fontSize: 16, color: red[5] }} />
        );
    }
  };

  const formatterOperator = useCallback(
    (operators: string) =>
      operators.replaceAll(',', '').replace('[', '').replace(']', ''),
    [],
  );

  const handleMoreInfo = useCallback(
    (action: IAction) => {
      if (action?.operator) {
        if (action.actionType === 6 && action.operator === '[]') {
          return t('operators-reset');
        }
        return `${formatterOperator(action.operator || '')}`;
      }
      if (action.idContract) {
        return `${t('contract')} ${action.idContract}`;
      }
      return undefined;
    },
    [formatterOperator, t],
  );

  return (
    <CustomCard
      title={t('inventory-detailed:commands-history')}
      loading={!data}
      bodyStyle={{ padding: 24 }}
    >
      {data && data?.length > 0 ? (
        <Timeline
          mode="right"
          style={{
            paddingTop: 10,
          }}
        >
          {data?.map(action => {
            const infoDetails = handleMoreInfo(action);
            return (
              <Timeline.Item
                label={
                  <>
                    <div style={{ paddingLeft: 10 }}>
                      {formatDate(
                        new Date(action.createdAt),
                        'dd/MM/yyyy HH:mm',
                      )}
                    </div>
                    {action.flagProcessed !== 0 && (
                      <Text type="secondary">
                        {t(
                          `status.${action.actionType}.${action.flagProcessed}`,
                        )}
                      </Text>
                    )}
                  </>
                }
                dot={getDotByStatus(action.flagProcessed)}
                key={action.id}
              >
                <div style={{ paddingRight: 10 }}>
                  <Row justify="end">{t(`labels.${action?.actionType}`)}</Row>
                  {infoDetails && (
                    <Text type="secondary">
                      <div>{infoDetails}</div>
                    </Text>
                  )}
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
      ) : (
        <Paragraph style={{ padding: '16px' }}>
          <div>{t('no-data')}</div>
        </Paragraph>
      )}
    </CustomCard>
  );
};

export default DeviceActionsTimeline;
